// Every baby this account can open, with enough signal to tell them apart.
//
// This exists because the app used to auto-pick one. If an account had more
// than one membership — a real baby plus a test baby from setup night — the
// picker below is the only thing standing between a caretaker and an empty
// tracker that looks like weeks of data vanished. The entry count and last-entry
// time are the tell: the real baby is the one with hundreds of entries and a
// last feed an hour ago.

import { supabase } from "./supabase";
import type { CaretakerRole } from "../routes/BabyShell";

export type MyBaby = {
  id: string;
  name: string;
  birthday: string;
  due_date: string | null;
  photo_data: string | null;
  role: CaretakerRole;
  entryCount: number;
  lastEntryAt: string | null;
};

export async function listMyBabies(userId: string): Promise<MyBaby[]> {
  const { data: memberships, error } = await supabase
    .from("baby_caretakers")
    .select("baby_id, role, created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: true });
  if (error || !memberships || memberships.length === 0) return [];

  const ids = memberships.map((m) => m.baby_id as string);
  const { data: babies } = await supabase
    .from("babies")
    .select("id, name, birthday, due_date, photo_data")
    .in("id", ids);
  if (!babies) return [];

  // Two head-only queries per baby: a count and the newest `at`. Neither pulls
  // event rows over the wire, so this stays cheap even after months of logging.
  const stats = await Promise.all(
    ids.map(async (id) => {
      const [{ count }, { data: newest }] = await Promise.all([
        supabase.from("events").select("id", { count: "exact", head: true }).eq("baby_id", id),
        supabase.from("events").select("at").eq("baby_id", id).order("at", { ascending: false }).limit(1),
      ]);
      return {
        id,
        entryCount: count ?? 0,
        lastEntryAt: newest && newest.length > 0 ? (newest[0].at as string) : null,
      };
    }),
  );

  const statById = new Map(stats.map((s) => [s.id, s]));

  const rows: MyBaby[] = babies.map((b) => {
    const stat = statById.get(b.id as string);
    const membership = memberships.find((m) => m.baby_id === b.id);
    return {
      id: b.id as string,
      name: b.name as string,
      birthday: b.birthday as string,
      due_date: (b.due_date as string | null) ?? null,
      photo_data: (b.photo_data as string | null) ?? null,
      role: (membership?.role as CaretakerRole) ?? "caretaker",
      entryCount: stat?.entryCount ?? 0,
      lastEntryAt: stat?.lastEntryAt ?? null,
    };
  });

  return sortByRecency(rows, ids);
}

/**
 * Most recently used baby first — an abandoned test baby sinks to the bottom.
 * Babies with no entries at all sort last, oldest membership first among them.
 * `membershipOrder` is the baby ids in join order, used only as a tiebreak.
 */
export function sortByRecency<T extends { id: string; lastEntryAt: string | null }>(
  rows: T[],
  membershipOrder: string[],
): T[] {
  const rank = new Map(membershipOrder.map((id, i) => [id, i]));
  return [...rows].sort((a, b) => {
    if (a.lastEntryAt && b.lastEntryAt) return b.lastEntryAt.localeCompare(a.lastEntryAt);
    if (a.lastEntryAt) return -1;
    if (b.lastEntryAt) return 1;
    return (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0);
  });
}

export type Landing =
  | { kind: "create" }
  | { kind: "pick" }
  | { kind: "baby"; id: string };

/**
 * Where "/" sends a signed-in account. The one rule that matters: never guess
 * between two babies. The old code took the oldest membership, which for an
 * account whose first baby was a throwaway test one silently opened an empty
 * tracker — indistinguishable, to the person holding the phone, from weeks of
 * logs being deleted.
 */
export function chooseLanding(babies: Pick<MyBaby, "id">[], pointer: string | null): Landing {
  if (babies.length === 0) return { kind: "create" };
  if (pointer && babies.some((b) => b.id === pointer)) return { kind: "baby", id: pointer };
  if (babies.length === 1) return { kind: "baby", id: babies[0].id };
  return { kind: "pick" };
}
